<template>
  <div class="year-range-slider-container overflow-hidden">
    <!-- Year label at top left -->
    <div class="flex justify-start mb-2" :class="isRTL ? 'justify-end' : 'justify-start'">
      <span class="text-sm font-bold text-gray-900 truncate">{{ yearLabel }}</span>
    </div>
    
    <!-- Slider track container -->
    <div class="relative w-full h-1 bg-gray-300 rounded-full overflow-visible flex items-center" ref="trackContainer" :dir="isRTL ? 'rtl' : 'ltr'">
      <!-- Selected range track (blue highlighted segment between min and max handles) - Removed per user request -->
      
      <!-- Min year handle input -->
      <input
        type="range"
        :min="minLimit"
        :max="maxLimit"
        :value="selectedRange.min"
        @input="onMinChange"
        @change="onMinChange"
        class="year-range-input year-range-input-min"
        :style="minInputStyle"
      />
      
      <!-- Max year handle input -->
      <input
        type="range"
        :min="minLimit"
        :max="maxLimit"
        :value="selectedRange.max"
        @input="onMaxChange"
        @change="onMaxChange"
        class="year-range-input year-range-input-max"
        :style="maxInputStyle"
      />
      
      <!-- Min year tooltip (blue bubble below handle) -->
      <div
        v-if="!isRangeMode || selectedRange.min !== selectedRange.max"
        class="absolute bg-[#0963FF] text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg z-40 pointer-events-none whitespace-nowrap"
        :style="minTooltipStyle"
      >
        <span class="block truncate">
          <template v-if="isRangeMode && selectedRange.min !== selectedRange.max">
            <!-- In RTL, min value is on the right, so it shows "From". In LTR, min is on the left, so it shows "From" -->
            {{ $t('search.from') || 'From' }}: {{ selectedRange.min }}
          </template>
          <template v-else>
            {{ selectedRange.min }}
          </template>
        </span>
        <!-- Small triangle pointing up -->
        <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-transparent border-b-[#0963FF]"></div>
      </div>
      
      <!-- Max year tooltip (blue bubble above handle) -->
      <div
        v-if="isRangeMode && selectedRange.min !== selectedRange.max"
        class="absolute bg-[#0963FF] text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg z-40 pointer-events-none whitespace-nowrap"
        :style="maxTooltipStyle"
      >
        <span class="block truncate">
          <!-- In RTL, max value is on the left, so it shows "To". In LTR, max is on the right, so it shows "To" -->
          {{ $t('general.to') || 'To' }}: {{ selectedRange.max }}
        </span>
        <!-- Small triangle pointing down -->
        <div class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-[#0963FF]"></div>
      </div>
    </div>
    
    <!-- Labels below slider -->
    <div class="flex justify-between items-start mt-8 relative min-h-[3rem] overflow-hidden">
      <!-- Left side: Min label -->
      <div class="flex flex-col flex-shrink-0 max-w-[30%]" :class="isRTL ? 'items-end' : 'items-start'">
        <span class="text-xs text-gray-700 font-medium truncate">{{ $t('search.start_from') }}</span>
        <span class="text-sm text-gray-900 font-semibold truncate">{{ minLimit }}</span>
      </div>
      
      <!-- Right side: Max label -->
      <div class="flex flex-col flex-shrink-0 max-w-[30%]" :class="isRTL ? 'items-start' : 'items-end'">
        <span class="text-xs text-gray-700 font-medium truncate">{{ $t('general.to') || 'To' }}</span>
        <span class="text-sm text-gray-900 font-semibold truncate">{{ maxLimit }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { useGlobalStore } from '@/store/Modules/global';

export default {
  name: 'YearRangeSlider',
  setup() {
    const global = useGlobalStore();
    return {
      global,
      isRTL: global?.localStorageCopy?.language === 'ar'
    };
  },
  props: {
    minLimit: {
      type: Number,
      required: true
    },
    maxLimit: {
      type: Number,
      required: true
    },
    selectedYearValue: {
      type: Number,
      default: null
    },
    selectedYearRange: {
      type: Object,
      default: null,
      validator(value) {
        return value === null || (typeof value === 'object' && typeof value.min === 'number' && typeof value.max === 'number');
      }
    },
    yearLabel: {
      type: String,
      default: 'سنة'
    }
  },
  data() {
    // Support both single year (selectedYearValue) and range (selectedYearRange)
    let initialRange;
    if (this.selectedYearRange !== null && typeof this.selectedYearRange === 'object') {
      // Range mode
      initialRange = {
        min: this.selectedYearRange.min ?? this.minLimit,
        max: this.selectedYearRange.max ?? this.maxLimit
      };
    } else if (this.selectedYearValue !== null) {
      // Single year mode (backward compatibility)
      initialRange = {
        min: this.selectedYearValue,
        max: this.selectedYearValue
      };
    } else {
      // Default to full range
      initialRange = {
        min: this.minLimit,
        max: this.maxLimit
      };
    }
    
    return {
      selectedRange: initialRange,
      isRangeMode: this.selectedYearRange !== null && typeof this.selectedYearRange === 'object'
    };
  },
  computed: {
    trackStyle() {
      const minPercent = ((this.selectedRange.min - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;
      const maxPercent = ((this.selectedRange.max - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;
      
      // In range mode (selectedYearRange used), always highlight the range between min and max handles
      // In single year mode (selectedYearValue used), show highlight from start to selected year
      if (this.isRangeMode) {
        // Range mode: highlight the blue segment between min and max handles
        const rangeWidth = Math.max(0.5, maxPercent - minPercent); // Ensure minimum width
        
        if (this.isRTL) {
          // For RTL: position from right side
          const rightPos = 100 - maxPercent;
          return {
            right: `${rightPos}%`,
            width: `${rangeWidth}%`
          };
        } else {
          // For LTR: position from left side
          return {
            left: `${minPercent}%`,
            width: `${rangeWidth}%`
          };
        }
      } else {
        // Single year mode: highlight from start to selected year (old behavior)
        if (this.isRTL) {
          return {
            right: '0%',
            left: `${100 - minPercent}%`
          };
        } else {
          return {
            left: '0%',
            right: `${100 - minPercent}%`
          };
        }
      }
    },
    minInputStyle() {
      return {
        position: 'absolute',
        width: '100%',
        height: '4px',
        top: '50%',
        left: '0',
        transform: 'translateY(-50%)',
        zIndex: this.selectedRange.min > this.selectedRange.max ? 25 : 20,
        pointerEvents: 'auto',
        cursor: 'pointer',
        margin: 0,
        padding: 0
      };
    },
    maxInputStyle() {
      return {
        position: 'absolute',
        width: '100%',
        height: '4px',
        top: '50%',
        left: '0',
        transform: 'translateY(-50%)',
        zIndex: this.selectedRange.max > this.selectedRange.min ? 25 : 20,
        pointerEvents: 'auto',
        cursor: 'pointer',
        margin: 0,
        padding: 0
      };
    },
    minTooltipStyle() {
      const percent = ((this.selectedRange.min - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;
      
      if (this.isRTL) {
        // For RTL, adjust transform to keep tooltip visible at edges
        let transformX = '50%';
        if (percent < 10) {
          // Near left edge (which is max in RTL), shift right
          transformX = '0%';
        } else if (percent > 90) {
          // Near right edge (which is min in RTL), shift left
          transformX = '100%';
        }
        
        return {
          right: `${percent}%`,
          top: 'calc(100% + 8px)',
          transform: `translateX(${transformX})`,
          whiteSpace: 'nowrap'
        };
      } else {
        // For LTR, adjust transform to keep tooltip visible at edges
        let transformX = '-50%';
        if (percent < 10) {
          // Near left edge, shift right
          transformX = '0%';
        } else if (percent > 90) {
          // Near right edge, shift left
          transformX = '-100%';
        }
        
        return {
          left: `${percent}%`,
          top: 'calc(100% + 8px)',
          transform: `translateX(${transformX})`,
          whiteSpace: 'nowrap'
        };
      }
    },
    maxTooltipStyle() {
      const percent = ((this.selectedRange.max - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;
      
      if (this.isRTL) {
        // For RTL, adjust transform to keep tooltip visible at edges
        let transformX = '50%';
        if (percent < 10) {
          // Near left edge (which is max in RTL), shift right
          transformX = '0%';
        } else if (percent > 90) {
          // Near right edge (which is min in RTL), shift left
          transformX = '100%';
        }
        
        return {
          right: `${percent}%`,
          bottom: 'calc(100% + 8px)',
          transform: `translateX(${transformX})`,
          whiteSpace: 'nowrap'
        };
      } else {
        // For LTR, adjust transform to keep tooltip visible at edges
        let transformX = '-50%';
        if (percent < 10) {
          // Near left edge, shift right
          transformX = '0%';
        } else if (percent > 90) {
          // Near right edge, shift left
          transformX = '-100%';
        }
        
        return {
          left: `${percent}%`,
          bottom: 'calc(100% + 8px)',
          transform: `translateX(${transformX})`,
          whiteSpace: 'nowrap'
        };
      }
    }
  },
  watch: {
    selectedYearRange(newVal) {
      if (newVal !== null && typeof newVal === 'object') {
        this.isRangeMode = true;
        this.selectedRange = {
          min: Math.max(this.minLimit, Math.min(newVal.min, this.maxLimit)),
          max: Math.max(this.minLimit, Math.min(newVal.max, this.maxLimit))
        };
        // Ensure min <= max
        if (this.selectedRange.min > this.selectedRange.max) {
          const temp = this.selectedRange.min;
          this.selectedRange.min = this.selectedRange.max;
          this.selectedRange.max = temp;
        }
      }
    },
    selectedYearValue(newVal) {
      if (newVal !== null && this.selectedYearRange === null) {
        // Single year mode (backward compatibility)
        this.isRangeMode = false;
        this.selectedRange = {
          min: Math.max(this.minLimit, Math.min(newVal, this.maxLimit)),
          max: Math.max(this.minLimit, Math.min(newVal, this.maxLimit))
        };
      }
    },
    minLimit() {
      this.selectedRange.min = Math.max(this.minLimit, this.selectedRange.min);
      this.selectedRange.max = Math.max(this.minLimit, this.selectedRange.max);
      // Ensure min <= max
      if (this.selectedRange.min > this.selectedRange.max) {
        this.selectedRange.max = this.selectedRange.min;
      }
    },
    maxLimit() {
      this.selectedRange.min = Math.min(this.maxLimit, this.selectedRange.min);
      this.selectedRange.max = Math.min(this.maxLimit, this.selectedRange.max);
      // Ensure min <= max
      if (this.selectedRange.min > this.selectedRange.max) {
        this.selectedRange.min = this.selectedRange.max;
      }
    }
  },
  methods: {
    onMinChange(event) {
      const newVal = parseInt(event.target.value);
      
      if (!this.isRangeMode) {
        // Single year mode: keep both handles together
        this.selectedRange.min = Math.max(this.minLimit, Math.min(newVal, this.maxLimit));
        this.selectedRange.max = this.selectedRange.min;
        this.$emit('year-change', this.selectedRange.min);
      } else {
        // Range mode: ensure min doesn't exceed max
        const constrainedVal = Math.min(newVal, this.selectedRange.max);
        this.selectedRange.min = Math.max(this.minLimit, Math.min(constrainedVal, this.maxLimit));
        this.$emit('year-change', { min: this.selectedRange.min, max: this.selectedRange.max });
      }
    },
    onMaxChange(event) {
      const newVal = parseInt(event.target.value);
      
      if (!this.isRangeMode) {
        // Single year mode: keep both handles together
        this.selectedRange.max = Math.max(this.minLimit, Math.min(newVal, this.maxLimit));
        this.selectedRange.min = this.selectedRange.max;
        this.$emit('year-change', this.selectedRange.max);
      } else {
        // Range mode: ensure max doesn't go below min
        const constrainedVal = Math.max(newVal, this.selectedRange.min);
        this.selectedRange.max = Math.max(this.minLimit, Math.min(constrainedVal, this.maxLimit));
        this.$emit('year-change', { min: this.selectedRange.min, max: this.selectedRange.max });
      }
    }
  },
  mounted() {
    // Initialization is handled in data(), but ensure consistency
    if (this.selectedYearRange !== null && typeof this.selectedYearRange === 'object') {
      this.isRangeMode = true;
      this.selectedRange = {
        min: Math.max(this.minLimit, Math.min(this.selectedYearRange.min, this.maxLimit)),
        max: Math.max(this.minLimit, Math.min(this.selectedYearRange.max, this.maxLimit))
      };
      // Ensure min <= max
      if (this.selectedRange.min > this.selectedRange.max) {
        const temp = this.selectedRange.min;
        this.selectedRange.min = this.selectedRange.max;
        this.selectedRange.max = temp;
      }
    } else if (this.selectedYearValue !== null) {
      this.isRangeMode = false;
      const year = Math.max(this.minLimit, Math.min(this.selectedYearValue, this.maxLimit));
      this.selectedRange = {
        min: year,
        max: year
      };
    } else {
      this.isRangeMode = true; // Default to range mode when nothing specified
      this.selectedRange = {
        min: this.minLimit,
        max: this.maxLimit
      };
    }
  },
  beforeUnmount() {
    // Cleanup if needed
  }
};
</script>

<style scoped>
.year-range-slider-container {
  width: 100%;
  padding: 1rem 0;
  overflow: hidden;
  position: relative;
}

.year-range-input {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  outline: none;
}

.year-range-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #0963FF;
  border: 2px solid white;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(9, 99, 255, 0.3);
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  position: relative;
  z-index: 30;
  margin-top: -10px;
}

.year-range-input-min::-webkit-slider-thumb {
  z-index: 30;
}

.year-range-input-max::-webkit-slider-thumb {
  z-index: 31;
}

.year-range-input::-webkit-slider-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 3px 8px rgba(9, 99, 255, 0.4);
}

.year-range-input::-webkit-slider-thumb:active {
  transform: scale(1.2);
  box-shadow: 0 4px 10px rgba(9, 99, 255, 0.5);
}

.year-range-input::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #0963FF;
  border: 2px solid white;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(9, 99, 255, 0.3);
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  position: relative;
  z-index: 30;
  margin-top: -10px;
}

.year-range-input-min::-moz-range-thumb {
  z-index: 30;
}

.year-range-input-max::-moz-range-thumb {
  z-index: 31;
}

.year-range-input::-moz-range-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 3px 8px rgba(9, 99, 255, 0.4);
}

.year-range-input::-moz-range-thumb:active {
  transform: scale(1.2);
  box-shadow: 0 4px 10px rgba(9, 99, 255, 0.5);
}

.year-range-input::-webkit-slider-runnable-track {
  width: 100%;
  height: 4px;
  background: transparent;
  cursor: pointer;
  border: none;
}

.year-range-input::-moz-range-track {
  width: 100%;
  height: 4px;
  background: transparent;
  cursor: pointer;
  border: none;
}

/* Ensure the slider track container has proper height for centering */
.year-range-slider-container .relative {
  min-height: 4px;
  display: flex;
  align-items: center;
}
</style>
